import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { 
  Sparkles, 
  Shirt, 
  Gem, 
  ShoppingBag, 
  Home, 
  Droplet,
  Heart,
  Flame
} from "lucide-react";
import { Z_INDEX } from "@/constants/ui.constants";

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function MegaMenu({ isOpen, onClose, onMouseEnter, onMouseLeave }: MegaMenuProps) {
  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop overlay - only covers content below navbar */}
      <div 
        className="fixed left-0 right-0 bottom-0 top-20 bg-background/20 backdrop-blur-sm" 
        style={{ zIndex: Z_INDEX.MEGA_MENU - 1 }}
        onClick={onClose}
      />
      
      {/* Menu container - independent floating */}
      <div 
        className="fixed left-0 right-0 top-20"
        style={{ zIndex: Z_INDEX.MEGA_MENU }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* Menu panel */}
        <div className="mx-auto px-4 w-full max-w-[90vw]">
        <div className="bg-card border border-border rounded-lg shadow-lg p-8 w-full">
          <div className="grid grid-cols-3 gap-12">
            
            {/* Left Column: Jewelry & Accessories */}
            <div className="space-y-4">
              {/* Jewelry Section */}
              <div className="space-y-3">
                <Link 
                  to="/collections/jewelry" 
                  className="flex items-center gap-2 pb-2 border-b border-border group"
                  onClick={onClose}
                >
                  <Gem className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Jewelry</h3>
                </Link>
                <nav className="space-y-0">
                  <Link 
                    to="/collections/jewelry?filter=necklaces" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Necklaces
                  </Link>
                  <Link 
                    to="/collections/jewelry?filter=bracelets" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Bracelets
                  </Link>
                  <Link 
                    to="/collections/jewelry?filter=rings" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Rings
                  </Link>
                  <Link 
                    to="/collections/jewelry?filter=earrings" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Earrings
                  </Link>
                  <Link 
                    to="/collections/jewelry?filter=jewelry%20sets" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Jewelry Sets
                  </Link>
                </nav>
              </div>

              {/* Accessories Section */}
              <div className="space-y-3">
                <Link 
                  to="/collections/accessories" 
                  className="flex items-center gap-2 pb-2 border-b border-border group"
                  onClick={onClose}
                >
                  <ShoppingBag className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Accessories</h3>
                </Link>
                <nav className="space-y-0">
                  <Link 
                    to="/collections/bags" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Bags
                  </Link>
                  <Link 
                    to="/collections/shoes" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Shoes
                  </Link>
                  <Link 
                    to="/collections/accessories?filter=hats" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Hats & Scarves
                  </Link>
                  <Link 
                    to="/collections/accessories?filter=belts" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Belts
                  </Link>
                </nav>
              </div>
            </div>

            {/* Center Column: Apparel & Special Collections */}
            <div className="space-y-4">
              {/* Apparel Section */}
              <div className="space-y-3">
                <Link
                  to="/collections/apparel" 
                  className="flex items-center gap-2 pb-2 border-b border-border group"
                  onClick={onClose}
                >
                  <Shirt className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Apparel</h3>
                </Link>
                <nav className="space-y-0">
                  <Link 
                    to="/collections/tops" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Tops
                  </Link>
                  <Link 
                    to="/collections/bottoms" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Bottoms
                  </Link>
                  <Link 
                    to="/collections/dresses" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Dresses
                  </Link>
                  <Link 
                    to="/collections/one-pieces" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    One-Pieces
                  </Link>
                  <Link 
                    to="/collections/lounge-wear" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Lounge Wear
                  </Link>
                </nav>
              </div>

              {/* Special Collections Section */}
              <div className="space-y-3">
                <Link 
                  to="/collections" 
                  className="flex items-center gap-2 pb-2 border-b border-border group"
                  onClick={onClose}
                >
                  <Heart className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Special Collections</h3>
                </Link>
                <nav className="space-y-0">
                  <Link 
                    to="/collections/goddess" 
                    className="block text-muted-foreground hover:text-primary transition-colors group"
                    onClick={onClose}
                  >
                    <span className="flex items-center gap-2">
                      <span>Goddess Collection</span>
                      <Sparkles className="h-4 w-4 text-primary transition-opacity" />
                    </span>
                  </Link>
                  <Link 
                    to="/collections/hot-chocolate-designs" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Hot Chocolate Design 🎨
                  </Link>
                  <Link 
                    to="/collections/chrome-hearts" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Chrome Hearts 🖤
                  </Link>
                  <Link 
                    to="/collections/sale" 
                    className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Sale
                    <Flame className="h-4 w-4 text-orange-500" />
                  </Link>
                </nav>
              </div>
            </div>

            {/* Right Column: Lifestyle & Home, Bath & Body */}
            <div className="space-y-4">
              {/* Lifestyle & Home Section */}
              <div className="space-y-3">
                <Link 
                  to="/collections/lifestyle" 
                  className="flex items-center gap-2 pb-2 border-b border-border group"
                  onClick={onClose}
                >
                  <Home className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Lifestyle & Home</h3>
                </Link>
                <nav className="space-y-0">
                  <Link 
                    to="/collections/furniture" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Furniture & Seating
                  </Link>
                  <Link 
                    to="/collections/rugs" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Rugs
                  </Link>
                  <Link 
                    to="/collections/textiles" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Linens & Blankets
                  </Link>
                  <Link 
                    to="/collections/decor" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Decor & Accents
                  </Link>
                  <Link 
                    to="/collections/outdoors" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    Porch & Patio
                  </Link>
                </nav>
              </div>

              {/* Bath & Body Section */}
              <div className="space-y-3">
                <Link 
                  to="/collections/bath-body" 
                  className="flex items-center gap-2 pb-2 border-b border-border group"
                  onClick={onClose}
                >
                  <Droplet className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Bath & Body</h3>
                </Link>
                <nav className="space-y-0">
                  <Link 
                    to="/collections/bath-body" 
                    className="block text-muted-foreground hover:text-primary transition-colors"
                    onClick={onClose}
                  >
                    All Bath & Body
                  </Link>
                </nav>
              </div>
            </div>

          </div>

          {/* View All Collections CTA */}
          <div className="mt-6 pt-4 border-t border-border text-center">
            <Link 
              to="/collections" 
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
              onClick={onClose}
            >
              View All Collections
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
        </div>
      </div>
    </>,
    document.body
  );
}
